import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowPathIcon,
  ArrowRightIcon,
  ClipboardDocumentListIcon,
  ClockIcon,
  GiftTopIcon,
  KeyIcon,
  UserGroupIcon,
  UserPlusIcon,
} from "@heroicons/react/24/outline";
import { supabase } from "../../lib/supabase";
import { PageHeader, StatusPill } from "../../components/admin/adminPrimitives";

interface Counts {
  users: number;
  activeLicences: number;
  trialLicences: number;
  expiringSupport: number;
  expiredSupport: number;
  pendingTransfers: number;
  allowlistSize: number;
  waitlistSize: number;
}

interface RecentAccount {
  id: string;
  school_name: string;
  email: string;
  created_at: string;
}

const THIRTY_DAYS_MS = 30 * 86_400_000;

export function Overview() {
  const [counts, setCounts] = useState<Counts | null>(null);
  const [recent, setRecent] = useState<RecentAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const nowIso = new Date().toISOString();
      const soonIso = new Date(Date.now() + THIRTY_DAYS_MS).toISOString();

      // Kick every count in parallel so the dashboard feels snappy. Each
      // head count is a cheap `select count(*)` against a table an
      // admin has RLS SELECT on.
      const [
        users,
        activeLicences,
        trialLicences,
        expiringSupport,
        expiredSupport,
        pendingTransfers,
        allowlistSize,
        waitlistSize,
        recentRows,
      ] = await Promise.all([
        supabase
          .from("accounts")
          .select("id", { count: "exact", head: true }),
        supabase
          .from("licenses")
          .select("id", { count: "exact", head: true })
          .eq("status", "active"),
        supabase
          .from("licenses")
          .select("id", { count: "exact", head: true })
          .eq("status", "trial"),
        supabase
          .from("licenses")
          .select("id", { count: "exact", head: true })
          .eq("status", "active")
          .not("support_ends_at", "is", null)
          .gte("support_ends_at", nowIso)
          .lte("support_ends_at", soonIso),
        supabase
          .from("licenses")
          .select("id", { count: "exact", head: true })
          .eq("status", "active")
          .not("support_ends_at", "is", null)
          .lt("support_ends_at", nowIso),
        supabase
          .from("transfer_requests")
          .select("id", { count: "exact", head: true })
          .eq("status", "pending"),
        supabase
          .from("signup_allowlist")
          .select("email", { count: "exact", head: true }),
        supabase
          .from("waitlist")
          .select("id", { count: "exact", head: true }),
        supabase
          .from("accounts")
          .select("id, school_name, email, created_at")
          .order("created_at", { ascending: false })
          .limit(5),
      ]);

      if (cancelled) return;

      const firstError =
        users.error ||
        activeLicences.error ||
        trialLicences.error ||
        expiringSupport.error ||
        expiredSupport.error ||
        pendingTransfers.error ||
        allowlistSize.error ||
        waitlistSize.error ||
        recentRows.error;
      if (firstError) {
        console.error("Overview fetch error:", firstError);
        setError("Failed to load overview: " + firstError.message);
        setLoading(false);
        return;
      }

      setCounts({
        users: users.count ?? 0,
        activeLicences: activeLicences.count ?? 0,
        trialLicences: trialLicences.count ?? 0,
        expiringSupport: expiringSupport.count ?? 0,
        expiredSupport: expiredSupport.count ?? 0,
        pendingTransfers: pendingTransfers.count ?? 0,
        allowlistSize: allowlistSize.count ?? 0,
        waitlistSize: waitlistSize.count ?? 0,
      });
      setRecent((recentRows.data ?? []) as RecentAccount[]);
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div>
      <PageHeader
        title="Admin overview"
        description="At-a-glance counts across the whole product. Everything here lives behind the @roguelite.tech RLS gate."
      />

      {error && <p className="mb-4 text-sm text-red-600">{error}</p>}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Users"
          value={counts?.users}
          loading={loading}
          icon={<UserGroupIcon className="h-5 w-5" />}
          to="/admin/users"
        />
        <StatCard
          label="Active licences"
          value={counts?.activeLicences}
          loading={loading}
          icon={<KeyIcon className="h-5 w-5" />}
          to="/admin/licenses"
        />
        <StatCard
          label="Trials in progress"
          value={counts?.trialLicences}
          loading={loading}
          icon={<ClockIcon className="h-5 w-5" />}
          to="/admin/licenses?status=trial"
        />
        <StatCard
          label="Pending transfers"
          value={counts?.pendingTransfers}
          loading={loading}
          tone={counts?.pendingTransfers ? "gold" : undefined}
          icon={<ArrowPathIcon className="h-5 w-5" />}
          to="/admin/transfers"
        />
        <StatCard
          label="Support expiring soon"
          value={counts?.expiringSupport}
          loading={loading}
          tone={counts?.expiringSupport ? "gold" : undefined}
          icon={<ClockIcon className="h-5 w-5" />}
          to="/admin/licenses?support=expiring"
          hint="Active licence, <30 days to renewal"
        />
        <StatCard
          label="Support expired"
          value={counts?.expiredSupport}
          loading={loading}
          tone={counts?.expiredSupport ? "red" : undefined}
          icon={<ClockIcon className="h-5 w-5" />}
          to="/admin/licenses?support=expired"
          hint="Still works, not receiving updates"
        />
        <StatCard
          label="Allowlist size"
          value={counts?.allowlistSize}
          loading={loading}
          icon={<UserPlusIcon className="h-5 w-5" />}
          to="/admin/allowlist"
        />
        <StatCard
          label="Waitlist size"
          value={counts?.waitlistSize}
          loading={loading}
          icon={<ClipboardDocumentListIcon className="h-5 w-5" />}
          to="/admin/waitlist"
        />
      </div>

      <div className="mt-10">
        <h2 className="font-display text-lg font-semibold text-forest-900">
          Recent signups
        </h2>
        <div className="paper-card mt-4 rounded-xl border border-cream-300/60 bg-white p-1">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-forest-300 border-t-forest-700" />
            </div>
          ) : recent.length === 0 ? (
            <div className="px-6 py-10 text-center text-sm text-forest-500">
              No accounts yet.
            </div>
          ) : (
            <ul className="divide-y divide-cream-200">
              {recent.map((acc) => (
                <li key={acc.id}>
                  <Link
                    to={`/admin/users/${acc.id}`}
                    className="flex items-center justify-between px-5 py-3 hover:bg-cream-50"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-forest-900">
                        {acc.school_name || "(unnamed)"}
                      </p>
                      <p className="truncate text-xs text-forest-500">
                        {acc.email}
                      </p>
                    </div>
                    <span className="ml-4 shrink-0 text-xs text-forest-500">
                      {new Date(acc.created_at).toLocaleDateString("en-GB", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="mt-10">
        <h2 className="font-display text-lg font-semibold text-forest-900">
          Quick actions
        </h2>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <QuickAction
            to="/admin/gift"
            icon={<GiftTopIcon className="h-5 w-5" />}
            title="Gift a licence"
          />
          <QuickAction
            to="/admin/allowlist"
            icon={<UserPlusIcon className="h-5 w-5" />}
            title="Invite a school"
          />
          <QuickAction
            to="/admin/users"
            icon={<UserGroupIcon className="h-5 w-5" />}
            title="Manage users"
          />
        </div>
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  loading,
  icon,
  to,
  tone,
  hint,
}: {
  label: string;
  value: number | undefined;
  loading: boolean;
  icon: React.ReactNode;
  to: string;
  tone?: "gold" | "red";
  hint?: string;
}) {
  return (
    <Link
      to={to}
      className="paper-card group block rounded-xl border border-cream-300/60 bg-white p-5 shadow-sm transition-shadow hover:shadow-md"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2 text-forest-500">
          {icon}
          <span className="text-xs font-medium uppercase tracking-wide">
            {label}
          </span>
        </div>
        {tone && (
          <StatusPill tone={tone}>
            {tone === "gold" ? "needs review" : "attention"}
          </StatusPill>
        )}
      </div>
      <p className="mt-3 font-display text-3xl font-bold text-forest-900">
        {loading ? (
          <span className="inline-block h-8 w-12 animate-pulse rounded bg-cream-200" />
        ) : (
          (value ?? 0).toLocaleString()
        )}
      </p>
      {hint && <p className="mt-1 text-xs text-forest-500">{hint}</p>}
    </Link>
  );
}

function QuickAction({
  to,
  icon,
  title,
}: {
  to: string;
  icon: React.ReactNode;
  title: string;
}) {
  return (
    <Link
      to={to}
      className="paper-card flex items-center justify-between rounded-xl border border-cream-300/60 bg-white px-5 py-4 text-sm font-medium text-forest-800 shadow-sm transition-all hover:border-forest-300 hover:bg-cream-50"
    >
      <div className="flex items-center gap-3">
        <div className="text-forest-500">{icon}</div>
        {title}
      </div>
      <ArrowRightIcon className="h-4 w-4 text-forest-400" />
    </Link>
  );
}
